/**
 * Comparison Table Variant Selector Fix
 * Handles variant selection in comparison table add-to-cart buttons
 */
(function () {
  const PRODUCT_CACHE = new Map();

  /**
   * Fetch product data by handle
   */
  async function getProductData(handle) {
    if (PRODUCT_CACHE.has(handle)) {
      return PRODUCT_CACHE.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}.js`, {
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${handle}`);
      }

      const product = await response.json();
      PRODUCT_CACHE.set(handle, product);
      return product;
    } catch (error) {
      console.error('[Comparison Table] Error fetching product:', error);
      return null;
    }
  }

  /**
   * Find variant ID by option value (e.g., "White", "Black")
   */
  function findVariantIdByOption(product, optionValue) {
    if (!product || !product.variants) return null;

    // Try to find exact match
    const variant = product.variants.find(v => {
      if (!v.options || !Array.isArray(v.options)) return false;
      return v.options.some(opt =>
        String(opt).toLowerCase() === String(optionValue).toLowerCase()
      );
    });

    if (variant) return variant.id;

    // Fallback to first available variant
    const availableVariant = product.variants.find(v => v.available);
    return availableVariant ? availableVariant.id : (product.variants[0]?.id || null);
  }

  /**
   * Update button with variant ID
   */
  function updateButtonVariantId(button, variantId) {
    if (!button || !variantId) return;
    button.setAttribute('data-comparison-variant-id', String(variantId));
    button.setAttribute('data-comparison-handled', 'true');
    console.log('[Comparison Table] Updated button with variant ID:', variantId);
  }

  /**
   * Handle select change
   */
  async function handleSelectChange(select) {
    const wrap = select.closest('.add-to-cart-wrap');
    if (!wrap) return;

    const button = wrap.querySelector('.add-to-cart');
    if (!button) return;

    const productSummary = select.closest('.product-summary');
    if (!productSummary) return;

    const handle = productSummary.dataset.handle;
    const selectedValue = select.value;

    if (!handle || !selectedValue) {
      console.warn('[Comparison Table] Missing handle or selected value');
      return;
    }

    console.log('[Comparison Table] Select changed:', { handle, selectedValue });

    const product = await getProductData(handle);
    if (!product) return;

    const variantId = findVariantIdByOption(product, selectedValue);
    if (variantId) {
      updateButtonVariantId(button, variantId);
    }
  }

  /**
   * Initialize select with first option
   */
  async function initializeSelect(select) {
    const wrap = select.closest('.add-to-cart-wrap');
    if (!wrap) return;

    const button = wrap.querySelector('.add-to-cart');
    if (!button) return;

    const productSummary = select.closest('.product-summary');
    if (!productSummary) return;

    const handle = productSummary.dataset.handle;
    const selectedValue = select.value || (select.options[0]?.value);

    if (!handle || !selectedValue) return;

    const product = await getProductData(handle);
    if (!product) return;

    const variantId = findVariantIdByOption(product, selectedValue);
    if (variantId) {
      updateButtonVariantId(button, variantId);
    }
  }

  /**
   * Add variant to cart
   */
  async function addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: Number(quantity)
        }),
        credentials: 'same-origin'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.description || data.message || 'Failed to add to cart');
      }

      return await response.json();
    } catch (error) {
      console.error('[Comparison Table] Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Handle add to cart button click
   */
  async function handleAddToCartClick(event) {
    const button = event.target.closest('.add-to-cart');
    if (!button) return;

    // Only handle if this is a comparison table button
    const isComparisonTable = button.closest('.product-table-cell-container, .comparable-container');
    if (!isComparisonTable) return;

    // Stop the event from reaching cart-drawer.js
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    console.log('[Comparison Table] Handling add to cart click');

    const wrap = button.closest('.add-to-cart-wrap');
    if (!wrap) return;

    const select = wrap.querySelector('.select-variant');
    const productSummary = button.closest('.product-summary');
    if (!productSummary) return;

    const handle = productSummary.dataset.handle;

    let variantId = button.getAttribute('data-comparison-variant-id');

    // If no variant ID stored yet, get it from the select or product
    if (!variantId) {
      const product = await getProductData(handle);
      if (!product) {
        console.error('[Comparison Table] Could not fetch product data');
        return;
      }

      if (select) {
        const selectedValue = select.value;
        variantId = findVariantIdByOption(product, selectedValue);
      } else {
        // No select, use first available variant
        const availableVariant = product.variants.find(v => v.available);
        variantId = availableVariant ? availableVariant.id : (product.variants[0]?.id);
      }
    }

    if (!variantId) {
      console.error('[Comparison Table] No variant ID found');
      return;
    }

    console.log('[Comparison Table] Adding variant to cart:', variantId);

    try {
      // Disable button to prevent double clicks
      button.style.pointerEvents = 'none';
      button.classList.add('is-loading');
      const originalText = button.textContent;
      button.textContent = 'Adding...';

      await addToCart(variantId, 1);

      // Open cart drawer if available
      if (typeof window.openDrawer === 'function') {
        window.openDrawer();
      }

      // Refresh cart if available
      if (typeof window.fetchAndRender === 'function') {
        await window.fetchAndRender();
      }

      // Dispatch cart:added event
      document.dispatchEvent(new CustomEvent('cart:added', {
        detail: { variantId: variantId }
      }));

      console.log('[Comparison Table] Successfully added to cart');

      button.textContent = originalText;
    } catch (error) {
      console.error('[Comparison Table] Failed to add to cart:', error);
      alert('Error adding item to cart: ' + error.message);
    } finally {
      // Re-enable button
      button.style.pointerEvents = '';
      button.classList.remove('is-loading');
    }
  }

  /**
   * Setup all comparison table selects and buttons
   */
  function setupComparisonTableSelects() {
    const selects = document.querySelectorAll('.product-table-cell-container .add-to-cart-wrap .select-variant, .comparable-container .add-to-cart-wrap .select-variant');
    const buttons = document.querySelectorAll('.product-table-cell-container .add-to-cart-wrap .add-to-cart, .comparable-container .add-to-cart-wrap .add-to-cart');

    console.log('[Comparison Table] Found', selects.length, 'variant selects and', buttons.length, 'add to cart buttons');

    selects.forEach(select => {
      // Initialize with current/first option
      initializeSelect(select);

      // Listen for changes
      if (!select.dataset.comparisonInitialized) {
        select.addEventListener('change', () => handleSelectChange(select));
        select.dataset.comparisonInitialized = 'true';
      }
    });

    // Mark buttons as comparison table buttons
    buttons.forEach(button => {
      button.setAttribute('data-comparison-table', 'true');
      button.setAttribute('data-comparison-handled', 'true');
    });
  }

  /**
   * Setup mutation observer
   */
  function setupObserver() {
    if (!document.body) {
      console.warn('[Comparison Table] Body not ready, deferring observer setup');
      return;
    }

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length) {
          const hasComparisonTable = Array.from(mutation.addedNodes).some(node => {
            if (node.nodeType !== 1) return false;
            return node.classList?.contains('comparable-container') ||
                   node.classList?.contains('product-table-cell-container') ||
                   node.querySelector?.('.comparable-container') ||
                   node.querySelector?.('.product-table-cell-container');
          });

          if (hasComparisonTable) {
            console.log('[Comparison Table] Detected new comparison table, reinitializing...');
            setTimeout(setupComparisonTableSelects, 100);
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    console.log('[Comparison Table] Initializing...');

    // Install global click handler in capture phase (BEFORE cart-drawer.js)
    document.addEventListener('click', handleAddToCartClick, true);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setupComparisonTableSelects();
        setupObserver();
      });
    } else {
      setupComparisonTableSelects();
      setupObserver();
    }
  }

  init();
})();
